import React from "react";
import { getTdStyle } from "../../styles/attendanceStyles";
import StatusBadge from "./StatusBadge";
import RowActions from "./RowActions";
import OvertimeApprovalButton from "./OvertimeApprovalButton";
import { useVisibleColumns } from "../../../../hooks/useVisibleColumns";

const getOvertimeDisplay = (value) => {
  if (!value || value === "-" || value === "0h" || value === "0m") {
    return <span style={{ color: "#9ca3af" }}>-</span>;
  }
  return <span style={{ color: "#10b981", fontWeight: "600" }}>+{value}</span>;
};

const getTardinessDisplay = (value) => {
  if (!value || value === "-" || value === "0h" || value === "0m") {
    return <span style={{ color: "#9ca3af" }}>-</span>;
  }
  return <span style={{ color: "#ef4444", fontWeight: "600" }}>-{value}</span>;
};

const TableRow = ({
  record,
  theme,
  isRTL,
  t,
  language,
  viewType,
  companyConfigs,
  canResetCheckIn,
  canResetCheckOut,
  canRevertOriginal,
  canViewAttendance,
  // ── NEW ───────────────────────────────────────────────────────────────────
  canApproveOvertime,
  overtimeApprovalRequired,
  onApproveOvertime,
  onRejectOvertime,
  // ─────────────────────────────────────────────────────────────────────────
  getConfigForEmployee,
  getEmployeeName,
  updateAttendancein,
  updateAttendanceout,
  revertAttendanceToOriginal,
  fetchEmployeeHistory,
  showAlert,
  BASE_URL,
}) => {
  const tdStyle = getTdStyle(theme);

  // ✅ Admin-level column visibility (hiddenColumns registry). Must stay in
  // sync with the same keys gated in TableHeader.jsx (same order). Bypassed
  // roles (admin/superadmin) always see every column — the hook handles that.
  const attendanceColumns = [
    { key: "employeeName" },
    { key: "date" },
    { key: "checkIn" },
    { key: "checkOut" },
    { key: "hoursWorked" },
    { key: "status" },
    { key: "overtime" },
    { key: "tardiness" },
    { key: "client" },
    { key: "actions" },
  ];
  const visibleColumns = useVisibleColumns("attendance", "attendanceTable", attendanceColumns);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  return (
    <tr
      key={record.id}
      style={{
        borderBottom:
          theme === "dark"
            ? "1px solid rgba(59, 130, 246, 0.1)"
            : "1px solid rgba(59, 130, 246, 0.05)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background =
          theme === "dark"
            ? "rgba(59, 130, 246, 0.08)"
            : "rgba(59, 130, 246, 0.04)";
        e.currentTarget.style.boxShadow =
          theme === "dark"
            ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
            : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* <td style={tdStyle}>{record.employeeId}</td> */}
      {showColumn("employeeName") && (
      <td style={{ ...tdStyle, fontWeight: "500",verticalAlign: isRTL ? 'right' : 'left',
      justifyContent: isRTL ? 'right' : 'left',
      textAlign: isRTL ? 'right' : 'left', }}>
        {getEmployeeName(record.employeeId) ||
          record.employeeName ||
          (language === "ar"
            ? record.employee?.arabicName || record.employee?.name_ar || record.employee?.sr
            : record.employee?.name || record.employee?.sr || record.employee?.fullName || record.employee?.englishName) ||
          "-"}
      </td>
      )}
      {showColumn("date") && (
      <td style={tdStyle}>
        {new Date(record.date).toLocaleDateString(
          language === "ar" ? "ar-EG" : "en-US",
          { year: "numeric", month: "long", day: "numeric" }
        )}
      </td>
      )}
      {showColumn("checkIn") && (
      <td style={{ ...tdStyle, direction: "ltr", fontFamily: "'Courier New', monospace" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <span>{record.checkIn || "-"}</span>
          {record.editedByIn && (
            <span
              title={language === "ar" ? `عدّل بواسطة: ${record.editedByIn}` : `Edited by: ${record.editedByIn}`}
              style={{ fontSize: "10px", color: "#f59e0b", fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: "3px" }}
            >
               {record.editedByIn}
            </span>
          )}
        </div>
      </td>
      )}
      {showColumn("checkOut") && (
      <td style={{ ...tdStyle, direction: "ltr", fontFamily: "'Courier New', monospace" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <span>{record.checkOut || "-"}</span>
          {record.editedByOut && (
            <span
              title={language === "ar" ? `عدّل بواسطة: ${record.editedByOut}` : `Edited by: ${record.editedByOut}`}
              style={{ fontSize: "10px", color: "#f59e0b", fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: "3px" }}
            >
               {record.editedByOut}
            </span>
          )}
        </div>
      </td>
      )}
      {showColumn("hoursWorked") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {record.hoursWorked > 0
          ? `${Math.round(parseFloat(record.hoursWorked) * 10) / 10} ${t.hours}`
          : "-"}
      </td>
      )}
      {showColumn("status") && (
      <td style={{ ...tdStyle }}>
        <StatusBadge status={record.status} language={language} />
      </td>
      )}
      {showColumn("overtime") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {overtimeApprovalRequired && (record.overTime > 0 || record.overTime) ? (
          <OvertimeApprovalButton
            record={record}
            theme={theme}
            isRTL={isRTL}
            language={language}
            viewType={viewType}
            canApproveOvertime={canApproveOvertime}
            overtimeApprovalRequired={overtimeApprovalRequired}
            onApprove={onApproveOvertime}
            onReject={onRejectOvertime}
          />
        ) : (
          getOvertimeDisplay(record.overTime)
        )}
      </td>
      )}
      {showColumn("tardiness") && (
      <td style={{ ...tdStyle, fontWeight: "500" }}>
        {getTardinessDisplay(record.underTime)}
      </td>
      )}
      {showColumn("client") && (
      <td style={tdStyle}>{record.clientName || "-"}</td>
      )}
      {showColumn("actions") && (
      <td style={{ ...tdStyle }}>
        <RowActions
          record={record}
          theme={theme}
          isRTL={isRTL}
          t={t}
          language={language}
          viewType={viewType}
          companyConfigs={companyConfigs}
          canResetCheckIn={canResetCheckIn}
          canResetCheckOut={canResetCheckOut}
          canRevertOriginal={canRevertOriginal}
          canViewAttendance={canViewAttendance}
          // ── NEW ───────────────────────────────────────────────────────────────────
          canApproveOvertime={canApproveOvertime}
          overtimeApprovalRequired={overtimeApprovalRequired}
          onApproveOvertime={onApproveOvertime}
          onRejectOvertime={onRejectOvertime}
          // ─────────────────────────────────────────────────────────────────────────
          getConfigForEmployee={getConfigForEmployee}
          updateAttendancein={updateAttendancein}
          updateAttendanceout={updateAttendanceout}
          revertAttendanceToOriginal={revertAttendanceToOriginal}
          fetchEmployeeHistory={fetchEmployeeHistory}
          showAlert={showAlert}
          BASE_URL={BASE_URL}
        />
      </td>
      )}
    </tr>
  );
};

export default TableRow;